import {IFacetCounts, IFacetFieldCounts} from '../../../shared/shared-models/type-sense/type-sense-types';
import {StockItem, VatRates} from '../../../shared-utilities/models-old/datastructures';
import {ISearchableFields} from '../../../shared/shared-models/type-sense/default-searchable-fields';
import {IStore} from '../../../shared/shared-models/store/store';
import {IPriceBandPerStore} from '../../../shared/shared-models/price-banding/price-band';
import {
  applyPriceBanding,
  PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS,
} from '../../../shared/shared-utils/price/price-banding.utils';

/** NgRx key used for multi-store merged stock rows / pagination / facets. */
export const MULTI_STORE_STATE_KEY = '__multi_store__';

/** Typesense max per_page. */
export const MULTI_STORE_TYPESENSE_PAGE_SIZE = 250;

/** Max hits fetched per store for a deep global page (page * pageSize). */
export const MULTI_STORE_PREFIX_CAP = 2500;

/**
 * Fixed palette for store row colours. Assigned by sorted storeId so colours stay stable
 * when selection order changes.
 */
export const MULTI_STORE_COLOUR_PALETTE: string[] = [
  '#E3F2FD',
  '#E8F5E9',
  '#FFF3E0',
  '#F3E5F5',
  '#E0F7FA',
  '#FCE4EC',
  '#FFF8E1',
  '#EFEBE9',
  '#E8EAF6',
  '#F1F8E9',
  '#E0F2F1',
  '#FFFDE7',
];

export interface IMultiStoreHitRow {
  item: StockItem;
  textMatch: number;
}

/**
 * Builds a stable storeId → colour map from the selected store ids.
 */
export function buildStoreColourMap(storeIds: string[]): Record<string, string> {
  const sorted = [...storeIds].filter(Boolean).sort((a: string, b: string) => a.localeCompare(b));
  const map: Record<string, string> = {};
  sorted.forEach((storeId: string, index: number) => {
    if (index < MULTI_STORE_COLOUR_PALETTE.length) {
      map[storeId] = MULTI_STORE_COLOUR_PALETTE[index];
      return;
    }
    const hue = Math.round((360 * (index - MULTI_STORE_COLOUR_PALETTE.length)) / Math.max(1, sorted.length - MULTI_STORE_COLOUR_PALETTE.length));
    map[storeId] = `hsl(${hue} 55% 88%)`;
  });
  return map;
}

/**
 * Snaps edited prices to each row's own store bands. Unedited rows are left unchanged.
 */
export function applyMultiStorePriceBanding(
  items: StockItem[],
  isPriceBanding: boolean,
  bandsByStore: IPriceBandPerStore,
  vatByStore: Record<string, VatRates>,
  stores: IStore[],
): StockItem[] {
  if (!isPriceBanding || !items?.length) {
    return items;
  }
  const storesById = new Map<string, IStore>(
    stores.map((store: IStore): [string, IStore] => [store.storeId, store]),
  );
  return items.map((item: StockItem): StockItem => {
    const store = storesById.get(item.storeId);
    if (!store) {
      return item;
    }
    const banded = applyPriceBanding(
      [item],
      bandsByStore?.[item.storeId] || [],
      PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS,
      store,
      vatByStore?.[item.storeId] || ({} as VatRates),
    );
    return banded?.[0] ?? item;
  });
}

/**
 * Applies store palette colours onto stock rows (replaces IQ line colours).
 */
export function applyStoreColoursToStockItems(
  items: StockItem[],
  colourMap: Record<string, string>,
): StockItem[] {
  return items.map((item: StockItem) => {
    const colour = colourMap[item.storeId] || '';
    return {
      ...item,
      lineColourValue: colour,
      lineColourDescription: item.store || item.storeId || '',
    };
  });
}

/**
 * Contrast-aware row style using the store colour already on the item.
 */
export function getMultiStoreRowStyle(data: StockItem | undefined): Record<string, string> {
  const background = data?.lineColourValue || '';
  const textColor = background ? contrastTextForStoreColour(background) : '';
  return {
    backgroundColor: background,
    color: textColor,
    WebkitTextFillColor: textColor,
    fill: background ? 'black' : 'var(--gallix-color-white-contrast)',
  };
}

/**
 * Light store-palette colours use black text. Kept local so this module does not import GridUtils
 * (that pulls column defs and cell editors into the selector graph and cycles with settings selectors).
 */
function contrastTextForStoreColour(colour: string): string {
  if (colour.startsWith('hsl')) {
    return 'black';
  }
  const hex = colour.replace('#', '');
  const expanded = hex.length === 3 ? hex.split('').map((char: string) => char + char).join('') : hex;
  const value = parseInt(expanded, 16);
  if (Number.isNaN(value)) {
    return 'black';
  }
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? 'black' : 'white';
}

/**
 * Composite AG Grid row id so the same stock code in two stores is two rows.
 */
export function getMultiStoreRowId(data: StockItem | undefined): string {
  if (!data) {
    return '';
  }
  return `${data.storeId || ''}|${data.code ?? ''}`;
}

/** Refinement filter key holding selected regular supplier codes. */
export const MULTI_STORE_SUPPLIER_FILTER_FIELD = '40';

/** Typesense facet field holding regular supplier code counts. */
export const MULTI_STORE_SUPPLIER_FACET_FIELD = 'regularSuppCode';

const MULTI_STORE_SUPPLIER_KEY_SEPARATOR = '::';

/**
 * Supplier codes are only unique within a store, so multi-store supplier options are keyed by store and code.
 */
export function buildMultiStoreSupplierKey(storeId: string, code: string): string {
  return `${storeId}${MULTI_STORE_SUPPLIER_KEY_SEPARATOR}${code}`;
}

/**
 * Builds one supplier option per store and code, labelled with the store name.
 */
export function buildMultiStoreSuppliers(
  suppliersByStore: Record<string, ISearchableFields>,
  storeIds: string[],
  stores: IStore[],
): ISearchableFields {
  const storeNames = new Map<string, string>(stores.map((store: IStore): [string, string] => [store.storeId, store.name]));
  return storeIds.reduce<ISearchableFields>((merged: ISearchableFields, storeId: string) => {
    const storeName = storeNames.get(storeId) || storeId;
    Object.entries(suppliersByStore[storeId] ?? {}).forEach(([code, supplier]) => {
      merged[buildMultiStoreSupplierKey(storeId, code)] = {
        ...supplier,
        name: `${supplier?.name || code} (${storeName})`,
      };
    });
    return merged;
  }, {});
}

/**
 * Narrows the multi-store supplier filter to the plain supplier codes selected for one store.
 * Returns null when suppliers are selected only for other stores, so this store has no matching rows.
 */
export function getStoreScopedFilters(
  filters: Record<string, ISearchableFields>,
  storeId: string,
): Record<string, ISearchableFields> | null {
  const supplierFilter = filters?.[MULTI_STORE_SUPPLIER_FILTER_FIELD];
  if (!supplierFilter || Object.keys(supplierFilter).length === 0) {
    return filters;
  }
  const prefix = buildMultiStoreSupplierKey(storeId, '');
  const storeSuppliers = Object.entries(supplierFilter).reduce<ISearchableFields>((acc: ISearchableFields, [key, supplier]) => {
    if (key.startsWith(prefix)) {
      acc[key.slice(prefix.length)] = supplier;
    }
    return acc;
  }, {});
  if (Object.keys(storeSuppliers).length === 0) {
    return null;
  }
  return {...filters, [MULTI_STORE_SUPPLIER_FILTER_FIELD]: storeSuppliers};
}

/**
 * Drops the supplier selection so a store that was excluded from the grid can still
 * report its own suppliers for multi-select.
 */
export function filtersWithoutSupplierSelection(
  filters: Record<string, ISearchableFields>,
): Record<string, ISearchableFields> {
  if (!filters?.[MULTI_STORE_SUPPLIER_FILTER_FIELD]) {
    return filters;
  }
  const next: Record<string, ISearchableFields> = {...filters};
  delete next[MULTI_STORE_SUPPLIER_FILTER_FIELD];
  return next;
}

/**
 * Keeps only the regular-supplier facet from a store that is not contributing grid rows.
 */
export function supplierFacetCountsOnly(facets: IFacetCounts): IFacetCounts {
  const counts = facets?.[MULTI_STORE_SUPPLIER_FACET_FIELD];
  if (!counts) {
    return {};
  }
  return {[MULTI_STORE_SUPPLIER_FACET_FIELD]: counts};
}

/**
 * Re-keys one store's supplier facet counts to store-scoped supplier keys so counts are not summed across stores.
 */
export function scopeSupplierFacetCountsToStore(facets: IFacetCounts, storeId: string): IFacetCounts {
  const supplierCounts = facets?.[MULTI_STORE_SUPPLIER_FACET_FIELD];
  if (!supplierCounts) {
    return facets;
  }
  const scopedCounts = Object.keys(supplierCounts).reduce<IFacetFieldCounts>((acc: IFacetFieldCounts, code: string) => {
    acc[buildMultiStoreSupplierKey(storeId, code)] = supplierCounts[code];
    return acc;
  }, {});
  return {...facets, [MULTI_STORE_SUPPLIER_FACET_FIELD]: scopedCounts};
}

/**
 * Merges facet count maps by summing counts for the same field/value.
 */
export function mergeFacetCounts(facetMaps: IFacetCounts[]): IFacetCounts {
  const merged: IFacetCounts = {};
  facetMaps.forEach((facets: IFacetCounts) => {
    if (!facets) {
      return;
    }
    Object.keys(facets).forEach((field: string) => {
      const fieldCounts: IFacetFieldCounts = facets[field] || {};
      if (!merged[field]) {
        merged[field] = {};
      }
      Object.keys(fieldCounts).forEach((value: string) => {
        merged[field][value] = (merged[field][value] || 0) + (fieldCounts[value] || 0);
      });
    });
  });
  return merged;
}

/**
 * Sorts merged hits for a global page. Uses text_match when searching, otherwise the active Typesense sort field.
 */
export function sortMultiStoreHits(
  rows: IMultiStoreHitRow[],
  sortType: string,
  hasSearchQuery: boolean,
): IMultiStoreHitRow[] {
  const sorted = [...rows];
  if (hasSearchQuery) {
    sorted.sort((a: IMultiStoreHitRow, b: IMultiStoreHitRow) => {
      if (b.textMatch !== a.textMatch) {
        return b.textMatch - a.textMatch;
      }
      return compareStockItemsBySort(a.item, b.item, sortType || '1:asc');
    });
    return sorted;
  }
  sorted.sort((a: IMultiStoreHitRow, b: IMultiStoreHitRow) =>
    compareStockItemsBySort(a.item, b.item, sortType || '1:asc'),
  );
  return sorted;
}

function compareStockItemsBySort(a: StockItem, b: StockItem, sortType: string): number {
  const [field, direction] = sortType.split(':');
  const asc = direction !== 'desc';
  const fieldKey = resolveSortFieldKey(field);
  const aVal = a?.[fieldKey];
  const bVal = b?.[fieldKey];

  if (aVal == null && bVal == null) {
    return tieBreakByStoreAndCode(a, b);
  }
  if (aVal == null) {
    return asc ? 1 : -1;
  }
  if (bVal == null) {
    return asc ? -1 : 1;
  }

  let cmp = 0;
  if (typeof aVal === 'number' && typeof bVal === 'number') {
    cmp = aVal - bVal;
  } else {
    cmp = String(aVal).localeCompare(String(bVal), undefined, {numeric: true, sensitivity: 'base'});
  }
  if (cmp === 0) {
    return tieBreakByStoreAndCode(a, b);
  }
  return asc ? cmp : -cmp;
}

function resolveSortFieldKey(typesenseField: string): keyof StockItem {
  const fieldMap = new Map<string, keyof StockItem>([
    ['0', 'code'],
    ['1', 'desc'],
    ['2', 'onHand'],
    ['3', 'dep'],
    ['4', 'subDep'],
    ['5', 'sellPriIncl1'],
    ['6', 'sellPriExcl1'],
    ['7', 'latestCost'],
    ['40', 'regularSuppCode'],
  ]);
  return fieldMap.get(typesenseField) || (typesenseField as keyof StockItem) || 'desc';
}

function tieBreakByStoreAndCode(a: StockItem, b: StockItem): number {
  const storeCmp = String(a.storeId || '').localeCompare(String(b.storeId || ''));
  if (storeCmp !== 0) {
    return storeCmp;
  }
  return String(a.code ?? '').localeCompare(String(b.code ?? ''), undefined, {numeric: true});
}

/**
 * How many Typesense hits (from the start) each store must contribute to cover global page `page`.
 */
export function getPrefixNeededForPage(page: number, pageSize: number): number {
  const safePage = Math.max(1, page || 1);
  const safeSize = Math.max(1, pageSize || 100);
  return Math.min(safePage * safeSize, MULTI_STORE_PREFIX_CAP);
}

/**
 * True when the requested global page would need more than the per-store prefix cap.
 */
export function isMultiStorePageBeyondPrefixCap(page: number, pageSize: number): boolean {
  const safePage = Math.max(1, page || 1);
  const safeSize = Math.max(1, pageSize || 100);
  return safePage * safeSize > MULTI_STORE_PREFIX_CAP;
}
